import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  ApiDownloadButton,
  FlexContainer,
  MoreOptions,
  Pagination
} from 'components/common';
import { Box } from 'components/common/box';
import { StatCount } from 'components/common/stats';
import {
  FollowUsersList,
  FollowUsersListFilters,
  FollowUsersToggleCompleteInBatch
} from 'components/courses/follow';
import { UsersSendMessage } from 'components/users';

import type { GetTaskUsersRes } from 'shared-types/responses';
import type { PageState } from 'types';

export type FollowUsersFilters = Pick<
  PageState,
  'completionStatus' | 'courseAccess' | 'groupId' | 'page' | 'search'
>;

const FollowUsers = () => {
  const {
    messages,
    pageState: { course },
    serverConfig: { adminUrl }
  } = useReduxState();

  const router = useRouter();
  const queryParams = Object.keys(router.query);

  const { data, loading, updateData } = useApiData<GetTaskUsersRes>({
    url:
      `/admin/courses/${course.id}/task-users` +
      (queryParams.length
        ? `?${queryParams
            .map((param) => `${param}=${router.query[param]}`)
            .join('&')}`
        : ''),
    onlyIf: Boolean(course.id)
  });

  return !course ? null : (
    <>
      <Box
        headerContent={
          <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
            <UsersSendMessage
              buttonSize="header"
              emailButtonTarget={`${adminUrl}/courses/${course.slug}`}
              id="course-follow-users-send-message"
              userSelectionParams={{
                excludeUsersWithoutEmail: true,
                routerQueryParams: {
                  courseId: `${course.id}`,
                  ...router.query
                },
                userSelector: 'emails'
              }}
            />

            <ApiDownloadButton
              category="success"
              filename={`${course.slug}.xlsx`}
              icon="file-excel"
              size="header"
              text={messages.tasksExportTasksData}
              url={
                `/admin/courses/${course.id}/excel-export` +
                (queryParams.length
                  ? `?${queryParams
                      .map((param) => `${param}=${router.query[param]}`)
                      .join('&')}`
                  : '')
              }
            />
            <MoreOptions category="light" styleContentButtons={false}>
              <FollowUsersToggleCompleteInBatch
                markComplete={true}
                updateUsers={updateData}
              />
              <FollowUsersToggleCompleteInBatch
                markComplete={false}
                updateUsers={updateData}
              />
            </MoreOptions>
          </FlexContainer>
        }
        headerContentWrap={true}
        subHeaderContent={<FollowUsersListFilters />}
        loading={loading}
        padding={0}
        title={
          <div>
            {messages.commonUsers}
            <StatCount
              isLarge={true}
              style={{ display: 'inline-block', marginLeft: 5 }}
              value={data?.usersCount || 0}
            />
          </div>
        }
      >
        <FollowUsersList
          courseVersionTagsByVersionNumber={
            data?.courseVersionTagsByVersionNumber
          }
          users={data?.users || []}
          updateUsers={updateData}
        />
      </Box>

      <Pagination
        currentPage={router.query.page ? Number(router.query.page) : 1}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => {
          router.replace({ query: { ...router.query, page } });
        }}
      />
    </>
  );
};

export default FollowUsers;
